import { useState } from "react";
import {
    Box,
    Card,
    CardContent,
    Typography,
    Stack,
    Button,
    Grid,
    Chip,
    TextField,
    Divider,
    InputAdornment,
} from "@mui/material";
import FavoriteIcon from "@mui/icons-material/Favorite";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CoffeeIcon from "@mui/icons-material/Coffee";
import { colors } from "../theme/theme";
import { DONATIONS } from "../util/config";

const currencySymbol = (c) => ({ EUR: "€", USD: "$", GBP: "£" }[c] || c);

// ── Amount selector ────────────────────────────────────────────────────────
const AmountPicker = ({ amount, setAmount }) => {
    const isCustom = !DONATIONS.suggestedAmounts.includes(Number(amount));

    return (
        <Box>
            <Typography variant="overline" sx={{ color: colors.textMuted, display: "block", mb: 1 }}>
                Choose an amount
            </Typography>

            <Grid container spacing={1.5}>
                {DONATIONS.suggestedAmounts.map((val) => {
                    const selected = !isCustom && Number(amount) === val;
                    return (
                        <Grid item xs={4} sm={2.4} key={val}>
                            <Box
                                onClick={() => setAmount(val)}
                                sx={{
                                    cursor: "pointer",
                                    border: "2px solid",
                                    borderColor: selected ? colors.accent : colors.border,
                                    bgcolor: selected ? colors.accentSoft : colors.surface,
                                    borderRadius: 1.5,
                                    py: 1.5,
                                    textAlign: "center",
                                    transition: "all 0.15s",
                                    "&:hover": {
                                        borderColor: colors.accent,
                                        bgcolor: selected ? colors.accentSoft : "rgba(255,90,78,0.04)",
                                    },
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontFamily: '"Outfit"',
                                        fontWeight: 700,
                                        fontSize: 22,
                                        letterSpacing: "-0.01em",
                                        color: selected ? colors.accent : colors.text,
                                        lineHeight: 1,
                                    }}
                                >
                                    {currencySymbol(DONATIONS.currency)}{val}
                                </Typography>
                            </Box>
                        </Grid>
                    );
                })}
            </Grid>

            <Typography variant="overline" sx={{ color: colors.textMuted, display: "block", mt: 2, mb: 1 }}>
                Or enter a custom amount
            </Typography>
            <TextField
                fullWidth
                size="small"
                type="number"
                value={isCustom ? amount : ""}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="Custom amount"
                inputProps={{ min: 1, step: 1 }}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <Typography
                                sx={{
                                    fontFamily: '"Outfit"',
                                    fontWeight: 700,
                                    fontSize: 18,
                                    color: colors.textMuted,
                                }}
                            >
                                {currencySymbol(DONATIONS.currency)}
                            </Typography>
                        </InputAdornment>
                    ),
                }}
                sx={{
                    "& .MuiOutlinedInput-root": {
                        ...(isCustom && {
                            "& fieldset": { borderColor: colors.accent, borderWidth: 2 },
                        }),
                    },
                }}
            />
        </Box>
    );
};

// ── Payment method card ────────────────────────────────────────────────────
const MethodCard = ({ method, amount }) => {
    const enabled = Number(amount) > 0;
    const url = enabled ? method.buildUrl(amount, DONATIONS.currency) : "#";

    return (
        <Card
            sx={{
                position: "relative",
                opacity: enabled ? 1 : 0.6,
                pointerEvents: enabled ? "auto" : "none",
                transition: "all 0.15s",
                "&:hover": enabled ? { borderColor: colors.accent } : {},
            }}
        >
            <CardContent sx={{ p: 3 }}>
                <Stack direction="row" alignItems="center" spacing={2} mb={1.5}>
                    {/* PayPal-style mark */}
                    <Box
                        sx={{
                            width: 44, height: 44, borderRadius: 1.5,
                            bgcolor: "#003087",
                            color: "#FFC439",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            fontFamily: '"Outfit"', fontWeight: 800, fontSize: 18,
                        }}
                    >
                        P
                    </Box>
                    <Box flex={1}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <Typography sx={{ fontWeight: 700, fontSize: 16 }}>
                                {method.label}
                            </Typography>
                            {method.badge && (
                                <Chip
                                    label={method.badge}
                                    size="small"
                                    sx={{
                                        bgcolor: colors.accent,
                                        color: "white",
                                        fontFamily: '"JetBrains Mono"',
                                        fontSize: 9,
                                        fontWeight: 700,
                                        letterSpacing: "0.1em",
                                        height: 18,
                                    }}
                                />
                            )}
                        </Stack>
                        <Typography sx={{ fontSize: 13, color: colors.textMuted }}>
                            {method.description}
                        </Typography>
                    </Box>
                </Stack>

                <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    component="a"
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    disabled={!enabled}
                    endIcon={<OpenInNewIcon />}
                    sx={{ mt: 1 }}
                >
                    Donate {currencySymbol(DONATIONS.currency)}{amount || "—"} via {method.label}
                </Button>
            </CardContent>
        </Card>
    );
};

// ── How it works (step explainer) ──────────────────────────────────────────
const HowItWorks = () => {
    const steps = [
        {
            n: 1,
            title: "Pick an amount",
            body: "Choose one of the suggested amounts or enter your own. There is no minimum and no maximum.",
        },
        {
            n: 2,
            title: "Click a payment method",
            body: "PayPal is currently the only option. You'll be redirected to PayPal's secure checkout in a new tab.",
        },
        {
            n: 3,
            title: "Complete the payment",
            body: "Pay with PayPal balance, credit card, or bank transfer. You don't need a PayPal account for card payments.",
        },
        {
            n: 4,
            title: "Done",
            body: "Funds support hosting, the FastAPI editor service, and ongoing development of new tools.",
        },
    ];

    return (
        <Card>
            <CardContent sx={{ p: 3 }}>
                <Typography variant="overline" sx={{ color: colors.textMuted, display: "block", mb: 0.5 }}>
                    Step-by-step
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    How donating works
                </Typography>

                <Stack spacing={2.5}>
                    {steps.map((s) => (
                        <Stack direction="row" key={s.n} spacing={2}>
                            <Box
                                sx={{
                                    flexShrink: 0,
                                    width: 32, height: 32, borderRadius: "50%",
                                    bgcolor: colors.accentSoft, color: colors.accent,
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                    fontFamily: '"JetBrains Mono"', fontWeight: 700, fontSize: 13,
                                }}
                            >
                                {s.n}
                            </Box>
                            <Box>
                                <Typography sx={{ fontWeight: 600, fontSize: 15, mb: 0.25 }}>
                                    {s.title}
                                </Typography>
                                <Typography sx={{ fontSize: 13.5, color: colors.textMuted, lineHeight: 1.55 }}>
                                    {s.body}
                                </Typography>
                            </Box>
                        </Stack>
                    ))}
                </Stack>
            </CardContent>
        </Card>
    );
};

// ── FAQ ────────────────────────────────────────────────────────────────────
const FaqCard = () => {
    const faqs = [
        {
            q: "Do I need a PayPal account?",
            a: "No. On PayPal's checkout page, click 'Pay with debit or credit card' to pay without registering.",
        },
        {
            q: "Will I receive a receipt?",
            a: "Yes — PayPal automatically sends a payment confirmation to your email.",
        },
        {
            q: "Is the donation tax-deductible?",
            a: "Not currently. KM-BIZZ is a small independent operation, not a registered non-profit.",
        },
        {
            q: "Can I donate something other than money?",
            a: "Absolutely. Bug reports, feature ideas, GitHub stars, and word-of-mouth all help. Drop a note on the Feedback page.",
        },
    ];

    return (
        <Card>
            <CardContent sx={{ p: 3 }}>
                <Typography variant="overline" sx={{ color: colors.textMuted, display: "block", mb: 0.5 }}>
                    Common questions
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    FAQ
                </Typography>

                <Stack divider={<Divider />} spacing={2}>
                    {faqs.map((f, i) => (
                        <Box key={i}>
                            <Typography sx={{ fontWeight: 600, fontSize: 14, mb: 0.5 }}>
                                {f.q}
                            </Typography>
                            <Typography sx={{ fontSize: 13.5, color: colors.textMuted, lineHeight: 1.55 }}>
                                {f.a}
                            </Typography>
                        </Box>
                    ))}
                </Stack>
            </CardContent>
        </Card>
    );
};

// ── Main page ──────────────────────────────────────────────────────────────
export const DonatePage = () => {
    const [amount, setAmount] = useState(DONATIONS.defaultAmount);

    return (
        <Box>
            {/* Hero */}
            <Box mb={4}>
                <Typography variant="overline" sx={{ color: colors.textMuted }}>
                    Support the project
                </Typography>
                <Typography variant="h3" sx={{ fontWeight: 700, letterSpacing: "-0.02em", mb: 1 }}>
                    Keep the tools <Box component="span" sx={{ color: colors.accent }}>free.</Box>
                </Typography>
                <Typography sx={{ color: colors.textMuted, maxWidth: 720 }}>
                    KM-BIZZ PDF Tools is built and maintained by one developer, hosted in Germany,
                    and runs without ads, tracking, or paywalls. Donations cover hosting, the FastAPI
                    editor service, and time for new features.
                </Typography>
            </Box>

            <Grid container spacing={3}>
                {/* Main donation flow */}
                <Grid item xs={12} md={7}>
                    <Card sx={{ mb: 3 }}>
                        <CardContent sx={{ p: 3 }}>
                            <Stack direction="row" alignItems="center" spacing={1.5} mb={2.5}>
                                <Box
                                    sx={{
                                        width: 40, height: 40, borderRadius: 2,
                                        bgcolor: colors.accentSoft, color: colors.accent,
                                        display: "flex", alignItems: "center", justifyContent: "center",
                                    }}
                                >
                                    <FavoriteIcon />
                                </Box>
                                <Box>
                                    <Typography variant="overline" sx={{ color: colors.textMuted }}>
                                        One-time donation
                                    </Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.1 }}>
                                        Pick an amount and a payment method
                                    </Typography>
                                </Box>
                            </Stack>

                            <AmountPicker amount={amount} setAmount={setAmount} />
                        </CardContent>
                    </Card>

                    <Typography variant="overline" sx={{ color: colors.textMuted, display: "block", mb: 1.5 }}>
                        Payment methods
                    </Typography>

                    <Stack spacing={2} mb={3}>
                        {DONATIONS.methods.map((method) => (
                            <MethodCard key={method.id} method={method} amount={amount} />
                        ))}
                    </Stack>

                    {/* Coming soon hint */}
                    <Box
                        sx={{
                            border: `1px dashed ${colors.border}`,
                            borderRadius: 2,
                            p: 2.5,
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            bgcolor: colors.surfaceAlt,
                        }}
                    >
                        <CoffeeIcon sx={{ color: colors.textMuted }} />
                        <Box>
                            <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
                                More payment options coming
                            </Typography>
                            <Typography sx={{ fontSize: 13, color: colors.textMuted }}>
                                Stripe (credit card), Buy Me a Coffee, and crypto support are planned.
                            </Typography>
                        </Box>
                    </Box>
                </Grid>

                {/* Sidebar */}
                <Grid item xs={12} md={5}>
                    {/* What donations fund */}
                    <Card sx={{ mb: 2, bgcolor: colors.sidebarBg, color: "white", border: "none" }}>
                        <CardContent sx={{ p: 3 }}>
                            <Chip
                                label="WHERE IT GOES"
                                size="small"
                                sx={{
                                    bgcolor: colors.accent,
                                    color: "white",
                                    fontFamily: '"JetBrains Mono"',
                                    fontSize: 9,
                                    fontWeight: 700,
                                    letterSpacing: "0.1em",
                                    height: 20,
                                    mb: 2,
                                }}
                            />
                            <Stack spacing={1.5}>
                                {[
                                    { label: "Server hosting (dogado, Germany)", icon: <CheckCircleOutlineIcon fontSize="small" /> },
                                    { label: "Domain renewals + SSL certificates", icon: <CheckCircleOutlineIcon fontSize="small" /> },
                                    { label: "Editor backend compute (FastAPI / PyMuPDF)", icon: <CheckCircleOutlineIcon fontSize="small" /> },
                                    { label: "New tools and features", icon: <CheckCircleOutlineIcon fontSize="small" /> },
                                ].map((row, i) => (
                                    <Stack direction="row" key={i} alignItems="center" spacing={1.5}>
                                        <Box sx={{ color: colors.accent }}>{row.icon}</Box>
                                        <Typography sx={{ fontSize: 13.5, color: colors.sidebarText }}>
                                            {row.label}
                                        </Typography>
                                    </Stack>
                                ))}
                            </Stack>
                        </CardContent>
                    </Card>

                    <Stack spacing={2}>
                        <HowItWorks />
                        <FaqCard />
                    </Stack>
                </Grid>
            </Grid>
        </Box>
    );
};
